import * as z from 'zod'

import { FilingStatus, filingStatusSchema } from '#dictionary/filingStatuses'

import { TaxKey } from '../taxes'
import raw from './taxThresholds.json'

// Statutory income thresholds for two federal surtaxes:
// Additional Medicare (0.9%) and Net Investment Income Tax (3.8%). Both were
// added by the ACA in 2013, apply above filing-status-dependent thresholds,
// and have never been indexed for inflation — the values are stable law.
export const taxThresholdSchema = z.object({
  taxKey: z.enum([TaxKey.TaxAdditionalMedicare, TaxKey.TaxNetInvestmentIncome]),
  filingStatus: filingStatusSchema,
  taxRate: z.number().min(0),
  threshold: z.number().nonnegative(),
})

export type TaxThreshold = z.infer<typeof taxThresholdSchema>

export type TaxThresholdKey = TaxThreshold['taxKey']

export const TAX_THRESHOLDS: readonly TaxThreshold[] = z
  .array(taxThresholdSchema)
  .parse(raw)

const THRESHOLD_TAX_KEYS = [
  TaxKey.TaxAdditionalMedicare,
  TaxKey.TaxNetInvestmentIncome,
] as const

// Doubly-indexed for O(1) lookup by (taxKey, filingStatus). Built once at
// module load and hidden behind `getTaxThreshold` below — callers should
// not manipulate the index directly.
const THRESHOLD_INDEX: ReadonlyMap<
  TaxThresholdKey,
  ReadonlyMap<FilingStatus, TaxThreshold>
> = new Map(
  THRESHOLD_TAX_KEYS.map(key => [
    key,
    new Map(
      TAX_THRESHOLDS.filter(t => t.taxKey === key).map(t => [t.filingStatus, t])
    ),
  ])
)

export const getTaxThreshold = (
  taxKey: TaxThresholdKey,
  filingStatus: FilingStatus
): TaxThreshold | undefined => THRESHOLD_INDEX.get(taxKey)?.get(filingStatus)
